/**
 * Auth Interceptor
 *
 * Handles automatic token attachment and refresh for Axios requests.
 * Uses request queue to prevent multiple simultaneous refresh attempts.
 */

import { fetchAuthSession, signOut } from 'aws-amplify/auth';

// State for tracking refresh operations
let isRefreshing = false;
let failedQueue = [];

/**
 * Process the queue of failed requests after token refresh
 * @param {Error|null} error - Error if refresh failed, null if successful
 * @param {string|null} token - New token if refresh succeeded
 */
const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

/**
 * Get current auth token from Amplify
 * @returns {Promise<string|null>} The ID token or null
 */
async function getAuthToken() {
  try {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString() || null;
  } catch (error) {
    console.error('Error getting auth token:', error);
    return null;
  }
}

/**
 * Force refresh the auth token
 * @returns {Promise<string>} The new ID token
 */
async function refreshAuthToken() {
  const session = await fetchAuthSession({ forceRefresh: true });
  const token = session.tokens?.idToken?.toString();
  if (!token) {
    throw new Error('Failed to refresh token - no token received');
  }
  return token;
}

/**
 * Handle sign out and redirect to login
 */
async function handleSignOut() {
  try {
    await signOut();
  } catch (error) {
    console.error('Error during sign out:', error);
  }
  // The Amplify Authenticator component will handle redirect to login
}

/**
 * Setup interceptors on an Axios instance
 * @param {import('axios').AxiosInstance} axiosInstance - The Axios instance to configure
 */
export function setupInterceptors(axiosInstance) {
  // Request interceptor - attach token to all requests
  axiosInstance.interceptors.request.use(
    async (config) => {
      const token = await getAuthToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor - handle 401 errors with token refresh
  axiosInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      // Only handle 401 errors that haven't been retried
      if (error.response?.status !== 401 || originalRequest._retry) {
        return Promise.reject(error);
      }

      // If already refreshing, queue this request
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return axiosInstance(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      // Mark as retrying and start refresh
      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const newToken = await refreshAuthToken();

        // Update the failed request with new token
        originalRequest.headers.Authorization = `Bearer ${newToken}`;

        // Process queued requests
        processQueue(null, newToken);

        // Retry the original request
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        // Token refresh failed - process queue with error and sign out
        processQueue(refreshError, null);
        console.error('Token refresh failed:', refreshError);

        // Sign out user - session is invalid
        await handleSignOut();

        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
  );
}
